import React from 'react';
import {
  Activity, FileText, Globe, Sparkles, Trophy, Users,
} from 'lucide-react';
import { BarViz, DailyActivityChart, KpiCard, LoadingSkeleton } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RacePlansTab({ data }) {
  if (!data) return <LoadingSkeleton />;
  const maxGoalCount = Math.max(...(data.top_goals || []).map(g => g.count), 1);
  const maxExpCount = Math.max(...(data.experience_breakdown || []).map(e => e.count), 1);

  return (
    <div className="space-y-6" data-testid="admin-race-plans-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard icon={Trophy} label="Total Plans" value={data.total_plans} subtext="generated" color="#F59E0B" />
        <KpiCard icon={Users} label="Unique Users" value={data.unique_users} subtext="plan creators" color="#06B6D4" />
        <KpiCard icon={Globe} label="Shared Plans" value={data.shared_plans} subtext="publicly shared" color="#10B981" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Experience Level Breakdown */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-teal-500" /> Experience Levels
          </h3>
          {data.experience_breakdown && data.experience_breakdown.length > 0 ? (
            <BarViz
              items={data.experience_breakdown.map(e => ({ label: e.level, value: `${e.count} plans`, raw: e.count }))}
              maxVal={maxExpCount}
              color="#f59e0b"
            />
          ) : (
            <p className="text-gray-400 text-sm">No data yet</p>
          )}
        </div>

        {/* Top Fitness Goals */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-purple-500" /> Top Fitness Goals
          </h3>
          {data.top_goals && data.top_goals.length > 0 ? (
            <BarViz
              items={data.top_goals.map(g => ({ label: g.goal, value: `${g.count}x`, raw: g.count }))}
              maxVal={maxGoalCount}
              color="#8b5cf6"
            />
          ) : (
            <p className="text-gray-400 text-sm">No goals data yet</p>
          )}
        </div>
      </div>

      {/* Daily Plans Chart */}
      {data.daily_plans && data.daily_plans.length > 0 && (
        <DailyActivityChart
          data={data.daily_plans.map(d => ({ date: d.date, value: d.count, color: '#f59e0b' }))}
          label="Plans Created (Last 30 Days)"
        />
      )}

      {/* Recent Plans Table */}
      <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="recent-plans-table">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <FileText className="w-4 h-4 text-teal-500" /> Recent Plans
        </h3>
        {data.recent_plans && data.recent_plans.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 text-xs border-b border-gray-100">
                  <th className="pb-2">User</th>
                  <th className="pb-2">Experience</th>
                  <th className="pb-2">Race Date</th>
                  <th className="pb-2">Shared</th>
                  <th className="pb-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_plans.map((rp, i) => (
                  <tr key={rp.created_at} className="border-b border-gray-50 last:border-0">
                    <td className="py-2.5">
                      <p className="text-gray-700 text-xs font-medium">{rp.user_name}</p>
                      <p className="text-gray-400 text-[10px]">{rp.user_email}</p>
                    </td>
                    <td className="py-2.5 text-xs text-gray-600">{rp.experience_level || 'N/A'}</td>
                    <td className="py-2.5 text-xs text-gray-600">{rp.race_date || 'Not set'}</td>
                    <td className="py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${rp.is_shared ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-400'}`}>
                        {rp.is_shared ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="py-2.5 text-xs text-gray-400">{rp.created_at ? new Date(rp.created_at).toLocaleDateString() : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-400 text-sm">No plans generated yet</p>
        )}
      </div>
    </div>
  );
}
